/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Index from "./Pages/Teacher/Index";
import StudentProfile from "./Pages/Users/Students/StudentProfile";
import Login from "./Pages/Auth/Login";
import Register from "./Pages/Auth/Register";
import ForgotPassword from "./Pages/Auth/ForgotPassword";
import ResetPassword from "./Pages/Auth/ResetPassword";
import Tables from "./views/examples/Tables";
import Icons from "./views/examples/Icons";
import Assessment from "./Pages/Teacher/Assessment/CreateAssessment";
import ShowAssessments from "./Pages/Teacher/Assessment/ShowAssessments";
import ShowAssessmentDetails from "./Pages/Teacher/Assessment/ShowAssessmentDetails";
import ShowStudents from "./Pages/Teacher/ShowStudents";
import AddUser from "./Pages/CreateUser/CreateStudentProfile";
import StudentList from "./Pages/Principal/Students/StudentList";
import Grievance from "./Pages/StateOfficial/Tickets/Grievance";
import TicketDetails from "./Pages/StateOfficial/Tickets/TicketDetails";
import StateLevelAssessments from "./Pages/StateOfficial/Assessments/StateLevelAssessments";
import CreateAssessment from "./Pages/StateOfficial/Assessments/CreateAssessment";
import StateOfficialIndex from "./Pages/StateOfficial/StateOfficialIndex";
import ReportIndex from "./Pages/StateOfficial/Reports/ReportIndex";
import MonthlyStudentPerformance from "./Pages/StateOfficial/Reports/MonthlyStudentPerformance";
import MonthlyStudentContentProgress from "./Pages/StateOfficial/Reports/MonthlyStudentContentProgress";
import AverageAppUsageForStudent from "./Pages/StateOfficial/Reports/AverageAppUsageForStudent";
import StudentPerformanceImprovement from "./Pages/StateOfficial/Reports/StudentPerformanceImprovement";
import EnrolledStudents from "./Pages/StateOfficial/Reports/EnrolledStudents";
import EnrolledTeachers from "./Pages/StateOfficial/Reports/EnrolledTeachers";
import TicketReport from "./Pages/StateOfficial/Reports/TicketReport";
import Communication from "./Pages/StateOfficial/CommunicationSection/Communication";
import WeeklyReports from "./Pages/StateOfficial/Reports/WeeklyReports";
import AverageAppUsageForStudentsForCurrentWeek from "./Pages/StateOfficial/Reports/AverageAppUsageForStudentsForCurrentWeek";
import ActiveStudents from "./Pages/StateOfficial/Reports/ActiveStudents";
import EAdhigamInfo from "./Pages/EAdhigamInfo";
import StateDashBoard from "./Pages/StateOfficial/StateDashBoard";
import SchoolDashBoard from "./Pages/StateOfficial/Screens/SchoolDashBoard";
import BoDashBoard from "./Pages/BlockOfficer/BoDashBoard";
import DoDashBoard from "./Pages/DistrictOfficer/DoDashBoard";
import PrincipalDashBoard from "./Pages/Principal/PrincipalDashBoard";
import TopStateStudent from "./Pages/StateOfficial/Screens/TopStudent"
import TopStateTeacher from "./Pages/StateOfficial/Screens/TopTeacher"
import AllTableReports from "./Pages/StateOfficial/Reports/TableReports/AllTableReports"
import ProgressReport from "./Pages/StateOfficial/ProgressReport/ProgressReport";

// interface for routing
interface Route {
  icon?: string;
  name?: string;
  path: string;
  component?: any;
  layout?: string;
}

// path declaration
export const PATHS = {
  // paths for all routes
  stateOfficerDashboard: "/stateOfficer-index",
  register: "/auth/register",
  login: "/auth/login",
  forgotPassword: "/auth/forgot-password",
  resetPassword: "auth/reset-password",
  teacherDashboard: "/index",
  parentDashboard: "/parent-index",
  principalDashboard: "/principal-index",
  blockOfficerDashboard: "/blockOfficer-index",
  districtOfficerDashboard: "/districtOfficer-index",
  // stateOfficerDashboard: '/stateOfficer-index',
  superAdmin: "/grievance",
  createAssessment: "create-assessment",
  showAssessments: "/teacher/show-assessments",
  studentProfile: "/teacher/student-profile",
  studentProfileForPrincipal: "/principal/student-profile",
  ticketDetails: "/teacher/ticket",
  assessmentDetails: "show-assessments-details",
  showStudentList: "students", // for teacher,
  editStudentInfo: "edit-student-info", // for teacher
};

// principle routes
export const PRINCIPLE_PATHS = {
  studentList: "/principal/student-list",
};

// state official routes
export const STATE_OFFICIAL_PATHS = {
  stateOfficerDashboard: "/stateOfficer-index",
  showAssessments: "/stateOfficer/assessments",
  createAssessment: "/stateOfficer/create-assessment",
  assessmentDetails: "show-assessments-details",
  ticketDetails: "/stateOfficer/ticket",
  grievance: "/stateOfficer/grievance",
  studentProfile: "/stateOfficer/student-profile",
  reports: "/stateOfficer/reports",
  monthlyStudentPerformance: "/stateOfficer/student-performance",
  monthlyStudentProgress: "/stateOfficer/student-progress",
  averageAppUsageForStudents: "/stateOfficer/student-app-usage",
  averageAppUsageForStudentsForCurrentWeek:
    "/stateOfficer/current-week-app-usage",
  studentPerformanceImprovement:
    "/stateOfficer/student-performance-imporvement",
  active: "/stateOfficer/active-students",
  enrolledStudents: "/stateOfficer/enrolled-students",
  enrolledTeachers: "/stateOfficer/enrolled-teachers",
  ticketReport: "/stateOfficer/ticket-report",
  eAdhigamInfo: "/e-Adhigam",
  weeklyReports: "/stateOfficer/weekly-reports",
  stateDashBoard: "/stateOfficer/state-dashboard",
  districtDashBoard: "/stateOfficer/district-dashboard",
  blockDashBoard: "/stateOfficer/block-dashboard",
  schoolDashBoard: "/stateOfficer/school-dashboard",
  topStateStudent: "/stateOfficer/top-State-Student",
  topStateTeacher: "/stateOfficer/top-State-Teacher",
  totalRamedialTestAttempted: "/stateOfficer/total-ramedial-test-attempted"
};

/* 
  --- routes for each type of user role  ---
  authRoutes is for default(when user is not logged in)
*/
export const Routes: {
  teacherRoutes: Route[];
  parentRoutes: Route[];
  principalRoutes: Route[];
  blockOfficerRoutes: Route[];
  districtOfficerRoute: Route[];
  stateOfficerRoute: Route[];
  authRoutes: Route[];
  superAdmin: Route[];
} = {
  teacherRoutes: [
    {
      path: "/index",
      name: "Dashboard",
      icon: "ni ni-tv-2 text-white",
      component: Index,
      layout: "/teacher",
    },
    {
      path: "/create-assessment",
      name: "Assign Task",
      icon: "",
      component: Assessment,
      layout: "/teacher",
    },
    {
      path: "/show-assessments",
      name: "Tasks",
      icon: "fas fa-stream text-white",
      component: ShowAssessments,
      layout: "/teacher",
    },
    {
      path: "/show-assessments-details/:id",
      name: "Task Details",
      icon: "",
      component: ShowAssessmentDetails,
      layout: "/teacher",
    },
    {
      path: "/students",
      name: "Students",
      icon: "fas fa-users text-white",
      component: ShowStudents,
      layout: "/teacher",
    },
    {
      path: "/icons",
      name: "Icons",
      icon: "",
      component: Icons,
      layout: "/teacher",
    },
    {
      path: "/student-profile/:id",
      name: "Student Profile",
      icon: "",
      component: StudentProfile,
      layout: "/teacher",
    },
    // ticket routes
    {
      path: "/grievance",
      name: "Grievance",
      icon: "fas fa-ticket-alt text-white",
      component: Grievance,
      layout: "/teacher",
    },
    {
      path: "/ticket/:id",
      name: "Details",
      icon: "",
      component: TicketDetails,
      layout: "/teacher",
    },
    // {
    //   path: "/tables",
    //   name: "Tables",
    //   icon: "",
    //   component: Tables,
    //   layout: "/teacher",
    // },
    {
      path: "",
      name: "Logout",
      icon: "",
      component: "",
      layout: "/teacher",
    },
  ],
  parentRoutes: [
    {
      path: "/parent-index",
      name: "Icons",
      icon: "ni ni-planet text-blue",
      component: Icons,
      layout: "/parent",
    },
    {
      path: "/tables",
      name: "Tables",
      icon: "ni ni-bullet-list-67 text-red",
      component: Tables,
      layout: "/parent",
    },

    {
      path: "",
      name: "Logout",
      icon: "ni ni-bullet-list-67 text-red",
      component: "",
      layout: "/parent",
    },
  ],
  superAdmin: [
    {
      path: "/grievance",
      name: "Grievance",
      icon: "ni ni-tv-2 text-white",
      component: Grievance,
      layout: "/super-admin",
    },
    // {
    //   path: "/ticket/:id",
    //   name: "Details",
    //   icon: "",
    //   component: TicketDetails,
    //   layout: "/super-admin"
    // },
    {
      path: "",
      name: "Logout",
      icon: "",
      component: "",
      layout: "/super-admin",
    },
  ],
  principalRoutes: [
    {
      path: "/stateOfficer-index",
      name: "Overview",
      icon: "ni ni-tv-2 text-white",
      component: StateOfficialIndex,
      layout: "/principal",
    },
    // {
    //   path: "/principal-index",
    //   name: "school-Overview",
    //   icon: "ni ni-tv-2 text-white",
    //   component: PrincipalDashBoard,
    //   layout: "/principal",
    // },
    {
      path: "/stateOfficer-Progress-Report",
      name: "Review Reports",
      icon: "fas fa-file-invoice text-white",
      component: ProgressReport,
      layout: "/principal",
    },
    {
      path: "/reports",
      name: "Detailed Data (Excel)",
      icon: "fas fa-file-invoice text-white",
      component: AllTableReports,
      layout: "/principal",
    },
    {
      path: "/e-Adhigam",
      name: "About e-Adhigam",
      icon: "fas fa-book text-white",
      component: EAdhigamInfo,
      layout: "/principal",
    },
    // {
    //   path: "/principal/block",
    //   name: "Block Dashboard",
    //   icon: "ni ni-tv-2 text-white",
    //   component: BoDashBoard,
    //   layout: "/principal",
    // },
    // {
    //   path: "/principal-index",
    //   name: "Icons",
    //   icon: "ni ni-planet text-white",
    //   component: Icons,
    //   layout: "/principal",
    // },
    // {
    //   path: "/create-student",
    //   name: "Create Student",
    //   icon: "fas fa-plus-square text-white",
    //   component: AddUser,
    //   layout: "/principal",
    // },
    // {
    //   path: "/student-profile/:id",
    //   name: "Student Profile",
    //   icon: "",
    //   component: StudentProfile,
    //   layout: "/principal",
    // },
    // {
    //   path: "/edit-student-info/:id",
    //   name: "Edit Student Info",
    //   icon: "",
    //   component: AddUser,
    //   layout: "/principal",
    // },
    // {
    //   path: "/student-list",
    //   name: "Student List",
    //   icon: "fas fa-users text-white",
    //   component: StudentList,
    //   layout: "/principal",
    // },
    // {
    //   path: "",
    //   name: "Logout",
    //   icon: "",
    //   component: "",
    //   layout: "/principal",
    // },
  ],
  blockOfficerRoutes: [
    {
      path: "/stateOfficer-index",
      name: "Overview",
      icon: "ni ni-tv-2 text-white",
      component: StateOfficialIndex,
      layout: "/blockOfficer",
    },
    // {
    //   path: "/blockOfficer-index",
    //   name: "Block-Overview",
    //   icon: "ni ni-tv-2 text-white",
    //   component: BoDashBoard,
    //   layout: "/blockOfficer",
    // },
    {
      path: "/stateOfficer-Progress-Report",
      name: "Review Reports",
      icon: "fas fa-file-invoice text-white",
      component: ProgressReport,
      layout: "/blockOfficer",
    },
    {
      path: "/reports",
      name: "Detailed Data (Excel)",
      icon: "fas fa-file-invoice text-white",
      component: AllTableReports,
      layout: "/blockOfficer",
    },
    {
      path: "/e-Adhigam",
      name: "About e-Adhigam",
      icon: "fas fa-book text-white",
      component: EAdhigamInfo,
      layout: "/blockOfficer",
    },
    // {
    //   path: "/block-dashboard",
    //   name: "District Dashboard",
    //   icon: "ni ni-tv-2 text-white",
    //   component: DoDashBoard,
    //   layout: "/blockOfficer",
    // },
  ],
  districtOfficerRoute: [
    {
      path: "/stateOfficer-index",
      name: "Overview",
      icon: "ni ni-tv-2 text-white",
      component: StateOfficialIndex,
      layout: "/districtOfficer",
    },
    // {
    //   path: "/districtOfficer-index",
    //   name: "District-Overview",
    //   icon: "ni ni-tv-2 text-white",
    //   component: DoDashBoard,
    //   layout: "/districtOfficer",
    // },
    {
      path: "/stateOfficer-Progress-Report",
      name: "Review Reports",
      icon: "fas fa-file-invoice text-white",
      component: ProgressReport,
      layout: "/districtOfficer",
    },
    {
      path: "/reports",
      name: "Detailed Data (Excel)",
      icon: "fas fa-file-invoice text-white",
      component: AllTableReports,
      layout: "/districtOfficer",
    },
    {
      path: "/e-Adhigam",
      name: "About e-Adhigam",
      icon: "fas fa-book text-white",
      component: EAdhigamInfo,
      layout: "/districtOfficer",
    },
    // {
    //   path: "/district-dashboard",
    //   name: "State Dashboard",
    //   icon: "ni ni-bullet-list-67",
    //   component: StateDashBoard,
    //   layout: "/districtOfficer",
    // },
  ],
  stateOfficerRoute: [
    {
      path: "/stateOfficer-index",
      name: "Overview",
      icon: "ni ni-tv-2 text-white",
      component: StateOfficialIndex,
      layout: "/stateOfficer",
    },

    // {
    //   path: "/state-dashboard",
    //   name: "State Dashboard",
    //   icon: "ni ni-tv-2 text-white",
    //   component: StateDashBoard,
    //   layout: "/stateOfficer",
    // },
    // {
    //   path: "/district-dashboard",
    //   name: "District Dashboard",
    //   icon: "ni ni-tv-2 text-white",
    //   component: DoDashBoard,
    //   layout: "/stateOfficer",
    // },
    // {
    //   path: "/block-dashboard",
    //   name: "Block Dashboard",
    //   icon: "ni ni-tv-2 text-white",
    //   component: BoDashBoard,
    //   layout: "/stateOfficer",
    // },
    // {
    //   path: "/school-dashboard",
    //   name: "School Dashboard",
    //   icon: "ni ni-tv-2 text-white",
    //   component: PrincipalDashBoard,
    //   layout: "/stateOfficer",
    // },
    // Top 10 State Student-teacher Result Routes

    // {
    //   path: "/top-State-Student",
    //   name: "Top Student",
    //   icon: "",
    //   component: TopStateStudent,
    //   layout: "/stateOfficer",
    // },
    {
      path: "/top-State-Student/:type/:district/:block/:week_no",
      name: "Top Student",
      icon: "",
      component: TopStateStudent,
      layout: "/stateOfficer",
    },
    {
      path: "/top-State-Teacher/:name",
      name: "Top Teacher",
      icon: "",
      component: TopStateTeacher,
      layout: "/stateOfficer",
    },

    // reports routes
    {
      path: "/stateOfficer-Progress-Report",
      name: "Review Reports",
      icon: "fas fa-file-invoice text-white",
      component: ProgressReport,
      layout: "/stateOfficer",
    },
    {
      path: "/reports",
      name: "Detailed Data (Excel)",
      icon: "fas fa-file-invoice text-white",
      component: AllTableReports,
      layout: "/stateOfficer",
    },
    // {
    //   path: "/reports",
    //   name: "Reports",
    //   icon: "fas fa-file-invoice text-white",
    //   component: ReportIndex,
    //   layout: "/stateOfficer",
    // },
    {
      path: "/student-performance",
      name: "Report",
      icon: "",
      component: MonthlyStudentPerformance,
      layout: "/stateOfficer",
    },
    {
      path: "/student-progress",
      name: "Report",
      icon: "",
      component: MonthlyStudentContentProgress,
      layout: "/stateOfficer",
    },
    {
      path: "/student-app-usage",
      name: "Report",
      icon: "",
      component: AverageAppUsageForStudent,
      layout: "/stateOfficer",
    },
    {
      path: "/total-ramedial-test-attempted",
      name: "Report",
      icon: "",
      component: AllTableReports,
      layout: "/stateOfficer",
    },
    {
      path: "/current-week-app-usage",
      name: "Report",
      icon: "",
      component: AverageAppUsageForStudentsForCurrentWeek,
      layout: "/stateOfficer",
    },
    {
      path: "/student-performance-imporvement",
      name: "Report",
      icon: "",
      component: StudentPerformanceImprovement,
      layout: "/stateOfficer",
    },
    {
      path: "/active-students",
      name: "Report",
      icon: "",
      component: ActiveStudents,
      layout: "/stateOfficer",
    },
    {
      path: "/enrolled-students",
      name: "Report",
      icon: "",
      component: EnrolledStudents,
      layout: "/stateOfficer",
    },
    {
      path: "/enrolled-teachers",
      name: "Report",
      icon: "",
      component: EnrolledTeachers,
      layout: "/stateOfficer",
    },
    {
      path: "/ticket-report",
      name: "Report",
      icon: "",
      component: TicketReport,
      layout: "/stateOfficer",
    },

    // assessment
    // {
    //   path: "/assessments",
    //   name: "Tasks",
    //   icon: "fas fa-stream text-white",
    //   component: StateLevelAssessments,
    //   layout: "/stateOfficer",
    // },
    // {
    //   path: "/create-assessment",
    //   name: "Assign Task",
    //   icon: "",
    //   component: CreateAssessment,
    //   layout: "/stateOfficer",
    // },
    // {
    //   path: "/show-assessments-details/:id",
    //   name: "Task Details",
    //   icon: "",
    //   component: ShowAssessmentDetails,
    //   layout: "/stateOfficer",
    // },

    // weekly reports
    {
      path: "/weekly-reports",
      name: "Weekly Reports",
      // icon: "fas fa-file-invoice text-white",
      icon: "",
      component: WeeklyReports,
      layout: "/stateOfficer",
    },

    // communication section
    // {
    //   path: "/communication",
    //   name: "Communication",
    //   icon: "fas fa-envelope text-white",
    //   component: Communication,
    //   layout: "/stateOfficer",
    // },

    // student profile
    {
      path: "/student-profile/:id",
      name: "Student Profile",
      icon: "",
      component: StudentProfile,
      layout: "/stateOfficer",
    },
    {
      path: "/e-Adhigam",
      name: "About e-Adhigam",
      icon: "fas fa-book text-white",
      component: EAdhigamInfo,
      layout: "/stateOfficer",
    },
    // {
    //   path: "",
    //   name: "Logout",
    //   icon: "ni ni-bullet-list-67 text-red",
    //   component: '',
    //   layout: "/stateOfficer",
    // },
    // {
    //   path: "/icons",
    //   name: "Icons",
    //   icon: "ni ni-planet text-blue",
    //   component: Icons,
    //   layout: "/stateOfficer",
    // },
    // {
    //   path: "/tables",
    //   name: "Tables",
    //   icon: "ni ni-bullet-list-67 text-red",
    //   component: Tables,
    //   layout: "/stateOfficer",
    // },
  ],
  authRoutes: [
    {
      path: "/login",
      name: "Login",
      icon: "ni ni-key-25 text-info",
      component: Login,
      layout: "/auth",
    },
    {
      path: "/register",
      name: "Register",
      icon: "ni ni-circle-08 text-pink",
      component: Register,
      layout: "/auth",
    },
    {
      path: "/forgot-password",
      name: "Forgot Password",
      // icon: "ni ni-circle-08 text-pink",
      component: ForgotPassword,
      layout: "/auth",
    },
    {
      path: "/reset-password",
      name: "Reset Password",
      // icon: "ni ni-circle-08 text-pink",
      component: ResetPassword,
      layout: "/auth",
    },
  ],
};

// export default routes;
